import { useState } from 'react';

import type {
  MessageFormState,
  PredefinedMessage,
  PredefinedMessageFormValues,
  UsePredefinedMessageFormsArgs,
} from '@/types/predefined-messages';

import { ADD, ALL, CLOSED, EDIT } from '@/constants';
import { usePredefinedMessageMutations } from '@/hooks/api/use-predefined-message-mutations';

export function usePredefinedMessageForms({
  selectedCategoryId,
  messages,
  setMessages,
  categories,
  categoryForm,
  setCategoryForm,
}: UsePredefinedMessageFormsArgs) {
  const [messageFormState, setMessageFormState] = useState<MessageFormState>({ action: CLOSED });

  const defaultCategoryForForm =
    selectedCategoryId === ALL ? (categories[0]?.id ?? 0) : selectedCategoryId;

  const selectedMessage =
    messageFormState.action === EDIT
      ? (messages.find(({ id }) => id === messageFormState.id) ?? null)
      : null;

  const showMessageForm =
    categoryForm === CLOSED &&
    (messageFormState.action === ADD || (messageFormState.action === EDIT && !!selectedMessage));

  let messageFormKey: string = CLOSED;

  if (messageFormState.action === ADD) {
    messageFormKey = ADD;
  } else if (messageFormState.action === EDIT) {
    messageFormKey = `edit-${messageFormState.id}`;
  }

  const messageFormMode: typeof ADD | typeof EDIT = messageFormState.action === ADD ? ADD : EDIT;

  const messageFormInitialValues =
    messageFormState.action === EDIT && selectedMessage
      ? {
          message: selectedMessage.message,
          predefinedMessageCategoryId: selectedMessage.predefinedMessageCategoryId,
          availableToIds: [...selectedMessage.availableToIds],
          recipientIds: [...selectedMessage.recipientIds],
          prioritized: selectedMessage.prioritized ?? false,
        }
      : undefined;

  function openAddMessageForm() {
    setCategoryForm(CLOSED);
    setMessageFormState({ action: ADD });
  }

  function closeMessageForm() {
    setMessageFormState({ action: CLOSED });
  }

  const { onCreateMessage, onEditMessage, deleteMessage } = usePredefinedMessageMutations({
    messageFormState,
    setMessageFormState,
    setMessages,
  });

  async function submitMessageForm(values: PredefinedMessageFormValues) {
    if (messageFormState.action === ADD) {
      await onCreateMessage(values);
    } else if (messageFormState.action === EDIT) {
      await onEditMessage(values);
    }
  }

  function onEditingMessage(message: PredefinedMessage) {
    setCategoryForm(CLOSED);
    setMessageFormState({ action: EDIT, id: message.id });
  }

  return {
    showMessageForm,
    messageFormKey,
    messageFormMode,
    messageFormInitialValues,
    defaultCategoryForForm,
    openAddMessageForm,
    closeMessageForm,
    submitMessageForm,
    onEditingMessage,
    deleteMessage,
  };
}
